/**
 * UpgradePrompt - Shows a prompt to upgrade when a feature/limit is blocked
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpCircleIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon as LockClosedSolid } from '@heroicons/react/24/solid';
import { useBilling } from '../../contexts/BillingContext';

// Feature display names and descriptions
const FEATURE_INFO = {
  dbt_enabled: {
    name: 'dbt Integration',
    description: 'Sync dbt models, tests, and sources with your data quality monitoring.',
  },
  lineage_enabled: {
    name: 'Data Lineage',
    description: 'Visualize data flow and understand upstream/downstream dependencies.',
  },
  anomaly_detection_enabled: {
    name: 'Anomaly Detection',
    description: 'Automatically detect unusual patterns in your data metrics.',
  },
  all_alert_channels: {
    name: 'All Alert Channels',
    description: 'Send alerts via Slack, Teams, webhooks, and more.',
  },
  tier4_enabled: {
    name: 'Data Profiling',
    description: 'Get detailed statistical profiling of your table data.',
  },
  tier6_enabled: {
    name: 'Query Usage Analytics',
    description: 'Analyze query history and usage patterns across your database.',
  },
  sso_enabled: {
    name: 'SSO/SAML',
    description: 'Enterprise single sign-on integration.',
  },
  teams_enabled: {
    name: 'Teams',
    description: 'Organize users into teams with role-based access.',
  },
  custom_scheduling: {
    name: 'Custom Scheduling',
    description: 'Set custom intervals for metadata collection and validation.',
  },
  auto_cached_rules_enabled: {
    name: 'Auto-Cached Rules',
    description: 'Automatically cache and optimize validation rules.',
  },
};

// Limit display names
const LIMIT_INFO = {
  connections: {
    name: 'Connections',
    singular: 'connection',
  },
  schemas: {
    name: 'Schemas',
    singular: 'schema',
  },
  profiled_tables: {
    name: 'Profiled Tables',
    singular: 'profiled table',
  },
  users: {
    name: 'Users',
    singular: 'user',
  },
  manual_rules: {
    name: 'Manual Rules',
    singular: 'manual rule',
  },
  custom_sql_rules: {
    name: 'Custom SQL Rules',
    singular: 'custom SQL rule',
  },
};

// Plan display info
const PLAN_INFO = {
  free: { name: 'Free', color: 'secondary' },
  starter: { name: 'Starter', color: 'primary' },
  growth: { name: 'Growth', color: 'accent' },
  scale: { name: 'Scale', color: 'success' },
};

const TRIAL_REACTIVATE_MAILTO =
  'mailto:contact@sparvi.io?subject=Sparvi%20reactivate%20after%20trial';

const UpgradePrompt = ({
  feature = null,
  limit = null,
  currentPlan = 'free',
  currentUsage = null,
  maxUsage = null,
  requiredTier = null,
  message = null,
  compact = false,
  className = '',
}) => {
  const { getUpgradeUrl, getNextTier, isTrialExpired } = useBilling();

  const nextTier = getNextTier();
  const upgradeUrl = getUpgradeUrl(requiredTier || nextTier);
  const planInfo = PLAN_INFO[currentPlan] || PLAN_INFO.free;
  const nextPlanInfo = PLAN_INFO[nextTier] || PLAN_INFO.starter;

  // Build the message
  let title = 'Upgrade Required';
  let description = message;

  if (feature && FEATURE_INFO[feature]) {
    const info = FEATURE_INFO[feature];
    title = info.name;
    description = description || info.description;
  } else if (limit && LIMIT_INFO[limit]) {
    const info = LIMIT_INFO[limit];
    title = `${info.name} Limit Reached`;
    if (currentUsage !== null && maxUsage !== null) {
      description = description ||
        `You've reached the ${info.singular} limit for your ${planInfo.name} plan (${currentUsage}/${maxUsage}).`;
    } else {
      description = description ||
        `You've reached the ${info.singular} limit for your ${planInfo.name} plan.`;
    }
  } else if (requiredTier) {
    title = `${PLAN_INFO[requiredTier]?.name || requiredTier} Plan Required`;
    description = description ||
      `This feature is available on the ${PLAN_INFO[requiredTier]?.name || requiredTier} plan and above.`;
  }

  // Compact version for inline use
  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 text-sm ${className}`}>
        <SparklesIcon className="h-4 w-4 text-amber-500" />
        <span className="text-secondary-600">{title}</span>
        {isTrialExpired ? (
          <a
            href={TRIAL_REACTIVATE_MAILTO}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Contact us
          </a>
        ) : (
          <Link
            to={upgradeUrl}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Upgrade
          </Link>
        )}
      </div>
    );
  }

  // Full prompt with card - redesigned to be more clearly a "blocked" state
  return (
    <div className={`relative rounded-lg border-2 border-amber-300 bg-amber-50 p-6 ${className}`}>
      {/* Locked badge in corner */}
      <div className="absolute -top-3 -right-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-md">
        <LockClosedSolid className="h-3.5 w-3.5" />
        LOCKED
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="bg-amber-100 rounded-full p-3 border-2 border-amber-300">
            <LockClosedIcon className="h-8 w-8 text-amber-600" />
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
            {title}
          </h3>
          <p className="mt-1 text-sm text-secondary-600">
            {description}
          </p>

          <div className="mt-3 p-3 bg-white rounded-md border border-amber-200">
            {isTrialExpired ? (
              <p className="text-sm text-secondary-700">
                Your Sparvi trial has ended. Reach out to reactivate the full
                plan and unlock this feature again.
              </p>
            ) : (
              <p className="text-sm text-secondary-700">
                <span className="font-medium">Your plan:</span>{' '}
                <span className="text-amber-700 font-semibold">{planInfo.name}</span>
                {nextTier && (
                  <>
                    {' → '}
                    <span className="font-medium">Required:</span>{' '}
                    <span className="text-primary-700 font-semibold">{nextPlanInfo.name} or higher</span>
                  </>
                )}
              </p>
            )}
          </div>

          {isTrialExpired ? (
            <div className="mt-4 flex items-center gap-4">
              <a
                href={TRIAL_REACTIVATE_MAILTO}
                className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                Contact us to reactivate
              </a>
            </div>
          ) : (
            nextTier && (
              <div className="mt-4 flex items-center gap-4">
                <Link
                  to={upgradeUrl}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Upgrade to {nextPlanInfo.name}
                </Link>

                <Link
                  to="/settings/billing"
                  className="text-sm text-secondary-600 hover:text-secondary-900 underline"
                >
                  Compare all plans
                </Link>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * UpgradeBadge - Small badge to show upgrade is available
 */
export const UpgradeBadge = ({ feature, className = '' }) => {
  const { isFeatureEnabled, getUpgradeUrl } = useBilling();

  if (isFeatureEnabled(feature)) {
    return null;
  }

  return (
    <Link
      to={getUpgradeUrl()}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 ${className}`}
    >
      <SparklesIcon className="h-3 w-3 mr-1" />
      Pro
    </Link>
  );
};

/**
 * LimitWarning - Shows a warning when approaching a limit
 */
export const LimitWarning = ({ limit, className = '' }) => {
  const { getLimitStatus, getUpgradeUrl } = useBilling();
  const status = getLimitStatus(limit);
  const info = LIMIT_INFO[limit];

  if (!status.is_near_limit || status.isUnlimited) {
    return null;
  }

  const percentage = Math.round(status.percentage);

  return (
    <div className={`flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg ${className}`}>
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-sm text-amber-700">
          You're using {percentage}% of your {info?.name?.toLowerCase() || limit} ({status.current}/{status.max})
        </p>
      </div>
      <Link
        to={getUpgradeUrl()}
        className="text-sm font-medium text-amber-700 hover:text-amber-800"
      >
        Upgrade
      </Link>
    </div>
  );
};

export default UpgradePrompt;
